import React, {useEffect, useState, useRef, useCallback} from 'react';
import {Link} from 'react-router';
import {List, OrderedSet} from 'immutable';
import {SortableContainerProvider} from '@albert-io/atomic';

import {ERROR_TYPES} from 'client/constants';
import {history} from 'client/history';
import sessionStore from 'client/Session/SessionStore';
import PracticeView from 'client/components/PracticeView/PracticeView.react';
import EmptyPracticeView from 'client/components/PracticeView/EmptyPracticeView/EmptyPracticeView.react';
import {canRevealSolution} from 'components/PracticeView/practiceView.utils';

import {MobilePracticeViewProvider} from 'components/PracticeView/MobilePracticeViewProvider.context';
import PaginatedResultsManager from 'components/PracticeView/helpers/paginatedResultsManager';
import FoldersModal from 'components/PracticeView/FoldersModal/FoldersModal.react';
import QuestionActions from 'components/PracticeView/QuestionActions/QuestionActions.react';
import {
  getPreviousQuestionState,
  getNextQuestionState
} from 'components/PracticeView/helpers/questionNavigation.utils';
import TitleBar from 'components/PracticeView/TitleBar/TitleBar.react';
import HeadingActions from 'components/PracticeView/HeadingActions/HeadingActions.react';
import LoadingView from 'components/PracticeView/LoadingView/LoadingView.react';
import {hasActiveFilter} from 'components/PracticeView/PracticeViewToolbar/Filters/filters.utils';

import {AssignmentModelV3 as Assignment} from 'resources/GeneratedModels/Assignment/AssignmentModel.v3';
import {QuestionModelV3 as Question} from 'src/resources/augmented/Question/QuestionModel.v3';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import {QuestionSetModelV1 as QuestionSet} from 'resources/augmented/QuestionSet/QuestionSetModel.v1';
import {GuessModelV1 as Guess} from 'resources/augmented/Guess/GuessModel.v1';

import Toolbar from './TeacherAssignmentPracticeViewToolbar.react';

import QuestionList from './TeacherAssignmentPracticeViewQuestionsList.react';

import {getQuestionSetsQuery, getAssignment} from './TeacherAssignmentPracticeView.queries';

interface Props {
  params: {
    assignmentId: string;
  };
  location: {
    state: {
      from?: string;
    };
    query: {
      sort?: string;
      filter?: string;
    };
  };
}

const getHeadingActions = () => {
  const actions = [<HeadingActions.QuestionFeedbackItem key='QuestionFeedbackItem' />];
  return <HeadingActions>{actions}</HeadingActions>;
};

const ActiveAssignmentPracticeView = ({params, location}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRearranging, setIsRearranging] = useState(false);
  const [assignment, setAssignment] = useState<Assignment>(null as Assignment);
  const [activeQuestion, setActiveQuestion] = useState<Question>({} as Question);
  const [activeQuestionSet, setActiveQuestionSet] = useState<QuestionSet>({} as QuestionSet);
  const [isSelectAllQuestionSetsMode, setIsSelectAllQuestionSetsMode] = useState(false);
  const [showFoldersModal, setShowFoldersModal] = useState(false);
  const [selectedQuestionSets, setSelectedQuestionSets] = useState<OrderedSet<QuestionSet>>(
    OrderedSet()
  );
  const [activeGuess, setActiveGuess] = useState<Guess>(null);
  const [questionSetsCount, setQuestionSetsCount] = useState(0);
  const [paginatedResultsManager, setPaginatedResultsManager] = useState<PaginatedResultsManager>(
    {} as PaginatedResultsManager
  );
  const [questionSets, setQuestionSets] = useState<List<QuestionSet>>(List());
  const {assignmentId} = params;
  const [classroomId, setClassroomId] = useState(null);
  const [, forceRerender] = useState({});

  const setActiveQuestionState = useCallback((question, questionSet) => {
    setActiveQuestion(question);
    setActiveQuestionSet(questionSet);
  }, []);

  const isFiltering = hasActiveFilter();

  useEffect(() => {
    if (assignment?.getClassrooms) {
      setClassroomId(assignment.getClassrooms().first().getId());
    }
  }, [assignment]);

  const resetPaginatedResultsManager = useCallback(() => {
    const query = getQuestionSetsQuery(assignmentId);
    const currentPaginatedResultsManager = new PaginatedResultsManager({
      initialQuery: query,
      onUpdate: (incomingQuestionSets) => {
        if (incomingQuestionSets) {
          setQuestionSets((prevQuestionSets) => {
            const updatedQuestionSets = prevQuestionSets.concat(
              incomingQuestionSets
            ) as List<QuestionSet>;
            const sortedUpdatedQuestionSets = updatedQuestionSets.sortBy((item) =>
              item!.getMeta().getAssignmentPosition()
            );
            return sortedUpdatedQuestionSets.toList();
          });
        } else {
          // This needs to be done because this is how paginatedResultsManager indicates that its loading.
          forceRerender({});
        }
      }
    });
    setPaginatedResultsManager(currentPaginatedResultsManager);

    const currentQuestionSet = currentPaginatedResultsManager.getResults().first() || null;
    const currentQuestion = currentQuestionSet?.getQuestions().first() || null;
    const currentQuestionSets = currentPaginatedResultsManager.getResults();

    const questionSetMeta = query.getResourceMetadata();
    const count = questionSetMeta.getIn(['page', 'total_entries'], 0);
    setQuestionSetsCount(count);

    setActiveQuestionState(currentQuestion, currentQuestionSet);
    setQuestionSets(currentQuestionSets);
    setIsLoading(false);
  }, [assignmentId, setActiveQuestionState]);

  useEffect(() => {
    const executeQuery = async () => {
      setIsLoading(true);
      let fetchAssignment;
      try {
        const query = getQuestionSetsQuery(assignmentId);
        await query.getResourcePromise();
        fetchAssignment = await getAssignment(assignmentId);
      } catch (e) {
        history.pushState(null, `/error?errorCode=${ERROR_TYPES.CRITICAL}`);
      }
      resetPaginatedResultsManager();
      setAssignment(fetchAssignment);
      setIsLoading(false);
    };
    executeQuery();
  }, [assignmentId, resetPaginatedResultsManager]);

  // taken directly from SPV
  const makeNavigateBackProps = ({
    paginatedQuestionsListManager,
    activeQuestionSet: questionSet,
    activeQuestion: question
  }: any) => {
    const currentQuestionSets = paginatedQuestionsListManager.getResults();
    if (currentQuestionSets.isEmpty()) {
      return makeNavigateProps(null);
    }

    const previousSetAndQuestion = getPreviousQuestionState({
      questionSets: currentQuestionSets,
      activeSet: questionSet,
      activeQuestion: question
    });
    return makeNavigateProps(previousSetAndQuestion);
  };

  // taken directly from SPV
  const makeNavigateNextProps = ({
    paginatedQuestionsListManager,
    activeQuestionSet: questionSet,
    activeQuestion: question
  }: any) => {
    const currentQuestionSets = paginatedQuestionsListManager.getResults();
    if (currentQuestionSets.isEmpty()) {
      return makeNavigateProps(null);
    }
    const nextSetAndQuestion = getNextQuestionState({
      questionSets: currentQuestionSets,
      activeSet: questionSet,
      activeQuestion: question
    });
    return makeNavigateProps(nextSetAndQuestion);
  };

  // taken directly from SPV
  const makeNavigateProps = (upcomingSetAndQuestion) => {
    if (!upcomingSetAndQuestion) {
      return {disabled: true, handleNavigation: undefined};
    }
    const {questionSet, question} = upcomingSetAndQuestion;
    return {
      disabled: false,
      handleNavigation: () => {
        scrollToQuestionByIdFunc.current(questionSet.getId());
        setActiveQuestionState(question, questionSet);
      }
    };
  };

  const getFooterActions = () => {
    const {disabled, handleNavigation} = makeNavigateNextProps({
      paginatedQuestionsListManager: paginatedResultsManager,
      activeQuestionSet,
      activeQuestion
    });

    // Make a list of question actions that we can conditionally push more actions to
    const actions = [
      <QuestionActions.MoreInfo
        questionSetId={activeQuestionSet.getId()}
        userId={sessionStore.getUserId()}
      />,
      <QuestionActions.Next onClick={handleNavigation} disabled={disabled} />
    ];
    if (
      canRevealSolution({
        question: activeQuestion,
        questionSet: activeQuestionSet,
        subject: activeQuestionSet.getSubject()
      })
    ) {
      actions.push(
        <QuestionActions.ToggleViewSolution
          question={activeQuestion}
          isViewingSolution={!!activeGuess}
          handleToggleSolution={(guess) =>
            activeGuess ? setActiveGuess(null) : setActiveGuess(guess)
          }
        />
      );
    }

    if (sessionStore.isSuper()) {
      actions.push(
        <QuestionActions.EditQuestionLink
          question={activeQuestion}
          questionSet={activeQuestionSet}
        />
      );
    }
    return (
      <QuestionActions questionStoreName={activeQuestion.getStore().getName()}>
        {actions}
      </QuestionActions>
    );
  };

  const activeSubject =
    activeQuestionSet && activeQuestionSet.getSubject && activeQuestionSet.getSubject();

  const scrollToQuestionByIdFunc = useRef((id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });
  });

  // taken directly from SPV
  const makeQuestionSetOnChangeHandler = (questionSet) => {
    return () => {
      const questionSetId = questionSet.getId();
      const newSelectedSets = selectedQuestionSets.includes(questionSetId)
        ? selectedQuestionSets.delete(questionSetId)
        : selectedQuestionSets.add(questionSetId);

      const isDeselectingLastSelectAllSet =
        isSelectAllQuestionSetsMode && newSelectedSets.size === questionSetsCount;

      if (isDeselectingLastSelectAllSet) {
        setSelectedQuestionSets(OrderedSet());
        setIsSelectAllQuestionSetsMode(false);
      } else {
        setSelectedQuestionSets(newSelectedSets);
      }
    };
  };

  if (isLoading || !assignment) {
    return <LoadingView />;
  }

  function toggleSelectAllQuestionSets(e) {
    setIsSelectAllQuestionSetsMode(e.target.checked);
    setSelectedQuestionSets(OrderedSet());
  }

  function getSelectedQuestionSetCount() {
    return isSelectAllQuestionSetsMode
      ? questionSetsCount - selectedQuestionSets.size
      : selectedQuestionSets.size;
  }

  const clearQuestionSetSelections = () => {
    setSelectedQuestionSets(OrderedSet());
    setIsSelectAllQuestionSetsMode(false);
  };

  function handleNameChange() {
    getAssignment(assignmentId).then(setAssignment);
  }

  function onSortEnd(oldIndex, newIndex) {
    paginatedResultsManager.moveQuestionSetToIndex(oldIndex, newIndex);
    setQuestionSets(paginatedResultsManager.getResults());
  }

  function invalidateQuestionsList() {
    invalidatePartialInterest({
      resourcePath: getQuestionSetsQuery(assignmentId).done().resourcePath
    });
  }

  function resetQuestionsListOrder() {
    invalidateQuestionsList();
    resetPaginatedResultsManager();
  }

  const selectedQuestionSetCount = getSelectedQuestionSetCount();

  if (!isLoading && questionSets.isEmpty()) {
    return (
      <EmptyPracticeView
        questionListHeader='No Questions Found'
        noQuestionSetsMessage='You have filtered out all questions from your assignment.'
        titleBarTitle={assignment.getName()}
        toolbar={<Toolbar isEmpty />}
        titleBarBackButton={
          <TitleBar.BackButton
            as={Link}
            label='Back To Assignments'
            to={location.state?.from || `/classes/${classroomId}/assignments`}
          />
        }
      />
    );
  }

  return (
    <SortableContainerProvider onSortEnd={onSortEnd}>
      <MobilePracticeViewProvider>
        <PracticeView
          activeQuestion={activeQuestion}
          activeQuestionSet={activeQuestionSet}
          activeSubject={activeSubject}
          activeQuestionStore={activeQuestion && activeQuestion.getStore()}
          activeGuess={activeGuess}
          questionSets={questionSets}
          isLoading={isLoading}
          titleBar={
            <TitleBar
              className='u-color_blue-500'
              title={assignment?.getName?.() || ''}
              backButton={
                <TitleBar.BackButton
                  as={Link}
                  label='Back To Assignments'
                  to={location.state?.from || `/classes/${classroomId}/assignments`}
                />
              }
            />
          }
          toolbar={
            <>
              <Toolbar
                assignment={assignment}
                questionSets={questionSets}
                isFiltering={isFiltering}
                isSorting={!!location.query.sort}
                clearQuestionSetSelections={clearQuestionSetSelections}
                selectedQuestionSets={selectedQuestionSets}
                selectedQuestionCount={selectedQuestionSetCount}
                toggleRearranging={() => setIsRearranging(!isRearranging)}
                invalidateQuestionsList={invalidateQuestionsList}
                isSelectAllQuestionSetsMode={isSelectAllQuestionSetsMode}
                resetQuestionsListOrder={resetQuestionsListOrder}
                isRearranging={isRearranging}
                onNameChange={handleNameChange}
                isLoading={isLoading}
                toggleFoldersModal={() => setShowFoldersModal(!showFoldersModal)}
              />
              {showFoldersModal && (
                <FoldersModal
                  selectedQuestionSetIds={selectedQuestionSets.toArray()}
                  userId={sessionStore.getUserId()}
                  closeModalFunc={() => setShowFoldersModal(false)}
                  isSelectAllQuestionSetsMode={isSelectAllQuestionSetsMode}
                  questionSetsQuery={getQuestionSetsQuery(assignmentId)}
                  selectedQuestionCount={selectedQuestionSetCount}
                  isReady={!isLoading}
                />
              )}
            </>
          }
          QuestionListComponent={
            <QuestionList
              makeQuestionSetOnChangeHandler={makeQuestionSetOnChangeHandler}
              selectedQuestionCount={selectedQuestionSetCount}
              selectedQuestionSets={selectedQuestionSets.toList()}
              scrollToQuestionById={scrollToQuestionByIdFunc}
              isLoading={isLoading}
              isRearranging={isRearranging}
              toggleSelectAllQuestionSets={toggleSelectAllQuestionSets}
              isSelectAllQuestionSetsMode={isSelectAllQuestionSetsMode}
              onQuestionClick={({question, questionSet}) => {
                return () => {
                  setActiveQuestionState(question, questionSet);
                };
              }}
              activeQuestion={activeQuestion}
              questionSets={questionSets}
              paginatedQuestionsListManager={paginatedResultsManager}
            />
          }
          questionRendererProps={{
            actions: getFooterActions(),
            rendererType: activeGuess ? 'PostGuess' : 'PreGuess',
            navigateBackProps: makeNavigateBackProps({
              paginatedQuestionsListManager: paginatedResultsManager,
              activeQuestionSet,
              activeQuestion
            }),
            navigateNextProps: makeNavigateNextProps({
              paginatedQuestionsListManager: paginatedResultsManager,
              activeQuestionSet,
              activeQuestion
            }),
            headingActions: getHeadingActions()
          }}
        />
      </MobilePracticeViewProvider>
    </SortableContainerProvider>
  );
};

export default ActiveAssignmentPracticeView;
